import {
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import type {
    TimeInputTimeType
} from "./components/timeInput/type";
import {
    type TimeSelectorGroupEvaluationConstantType,
    type TimeSelectorDayPeriodShiftConstantType,
    type TimeSelectorDurationTextConstantType,
    type TimeSelectorFallbackDateConstantType,
    type TimeSelectorSettledDraftConstantType,
    type TimeSelectorUnitMaximumConstantType,
    type TimeSelectorGranularityConstantType,
    type TimeSelectorActiveDraftConstantType,
    type TimeSelectorRangeErrorConstantType,
    type TimeSelectorGroupErrorConstantType,
    type TimeSelectorLabelColorConstantType,
    type TimeSelectorEvaluationConstantType,
    type TimeSelectorSameDraftsConstantType,
    type TimeSelectorNextDraftsConstantType,
    type TimeSelectorDayPeriodConstantType,
    type TimeSelectorCandidateConstantType,
    type TimeSelectorGroupDateConstantType,
    type TimeSelectorLimitTextConstantType,
    type TimeSelectorUnitValueConstantType,
    type TimeSelectorValueKeyConstantType,
    type TimeSelectorFieldKeyConstantType,
    type TimeSelectorUnitTextConstantType,
    type TimeSelectorNextDateConstantType,
    type TimeSelectorSameDateConstantType,
    type TimeSelectorGroupsConstantType,
    type TimeSelectorCommitConstantType,
    type TimeSelectorUnitsConstantType,
    type TimeSelectorGroupEvaluation,
    type TimeSelectorDayPeriodShift,
    type TimeSelectorLimitErrorType,
    type TimeSelectorRangeErrorType,
    type TimeSelectorDynamicStyle,
    type TimeSelectorGranularity,
    type TimeSelectorGroupError,
    type TimeSelectorRangeError,
    type TimeSelectorMomentUnit,
    type TimeSelectorEvaluation,
    type TimeSelectorDayPeriod,
    type TimeSelectorGroupType,
    type TimeSelectorFieldKey,
    type TimeSelectorDrafts,
    type TimeSelectorCommit,
    type TimeSelectorDraft,
    type UpdateDateType
} from "./type";
import {
    TIME_INPUT_DIGIT_COUNT
} from "./components/timeInput/stylesheet";
import type {
    Mutable
} from "../../types";
import type {
    DateTimePickerVariant
} from "../dateTimePicker/type";
import moment from "moment";
import {
    DATE_TIME_FORMAT_OPTIONS,
    getMomentLocale,
    formatDateTime
} from "../../helpers/locale";

export const TIME_SELECTOR_VARIANT_GROUPS: Record<DateTimePickerVariant, Array<TimeSelectorGroupType>> = {
    range: [
        "start",
        "end"
    ],
    rrule: [
        "single"
    ],
    single: [
        "single"
    ]
};

export const TIME_SELECTOR_UNITS: Array<TimeInputTimeType> = [
    "hours",
    "minutes",
    "seconds"
];

export const TIME_SELECTOR_UNIT_MAXIMUMS: Record<TimeInputTimeType, number> = {
    minutes: 59,
    seconds: 59,
    hours: 23
};

export const TIME_SELECTOR_TWELVE_HOUR_MAXIMUM = 12;

export const TIME_SELECTOR_TWELVE_HOUR_MINIMUM = 1;

export const TIME_SELECTOR_HALF_DAY_HOURS = 12;

export const TIME_SELECTOR_DAY_PERIODS: Array<TimeSelectorDayPeriod> = [
    "am",
    "pm"
];

export const TIME_SELECTOR_DAY_PERIOD_KEYS: Record<TimeSelectorDayPeriod, keyof NCoreUIKit.Translation> = {
    am: "time-selector-am",
    pm: "time-selector-pm"
};

export const TIME_SELECTOR_DAY_PERIOD_LABEL_KEY: keyof NCoreUIKit.Translation = "time-selector-day-period";

export const TIME_SELECTOR_MOMENT_UNITS: Record<TimeInputTimeType, TimeSelectorMomentUnit> = {
    minutes: "minute",
    seconds: "second",
    hours: "hour"
};

export const TIME_SELECTOR_GROUP_LABEL_KEYS: Record<TimeSelectorGroupType, keyof NCoreUIKit.Translation> = {
    single: "time-selector-label",
    start: "start-time",
    end: "end-time"
};

export const TIME_SELECTOR_FIELD_LABEL_KEYS: Record<TimeSelectorGroupType, Record<TimeInputTimeType, keyof NCoreUIKit.Translation>> = {
    start: {
        minutes: "time-selector-start-minute",
        seconds: "time-selector-start-second",
        hours: "time-selector-start-hour"
    },
    end: {
        minutes: "time-selector-end-minute",
        seconds: "time-selector-end-second",
        hours: "time-selector-end-hour"
    },
    single: {
        minutes: "time-selector-minute",
        seconds: "time-selector-second",
        hours: "time-selector-hour"
    }
};

export const TIME_SELECTOR_PLACEHOLDER_KEYS: Record<TimeInputTimeType, keyof NCoreUIKit.Translation> = {
    minutes: "time-selector-minute-placeholder",
    seconds: "time-selector-second-placeholder",
    hours: "time-selector-hour-placeholder"
};

export const TIME_SELECTOR_UNIT_RANGE_KEYS: Record<TimeInputTimeType, keyof NCoreUIKit.Translation> = {
    minutes: "time-selector-minute-range",
    seconds: "time-selector-second-range",
    hours: "time-selector-hour-range"
};

export const TIME_SELECTOR_LIMIT_KEYS: Record<TimeSelectorLimitErrorType, keyof NCoreUIKit.Translation> = {
    minDate: "time-selector-min-time",
    maxDate: "time-selector-max-time"
};

export const TIME_SELECTOR_RANGE_ERROR_KEYS: Record<TimeSelectorRangeErrorType, keyof NCoreUIKit.Translation> = {
    minimumGap: "selected-start-and-end-times-cannot-be-less-than-x-times-apart",
    maximumGap: "selected-start-and-end-times-cannot-exceed-x-intervals",
    endBeforeStart: "end-time-cannot-be-before-start-time",
    startAfterEnd: "start-time-cannot-be-after-end-time"
};

export const getTimeSelectorGroups = ({
    variant
}: TimeSelectorGroupsConstantType): Array<TimeSelectorGroupType> => {
    return TIME_SELECTOR_VARIANT_GROUPS[variant] ?? TIME_SELECTOR_VARIANT_GROUPS.single;
};

export const getTimeSelectorUnits = ({
    isWorkWithSeconds
}: TimeSelectorUnitsConstantType): Array<TimeInputTimeType> => {
    if(isWorkWithSeconds) {
        return TIME_SELECTOR_UNITS;
    }

    return TIME_SELECTOR_UNITS.filter((unit) => unit !== "seconds");
};

export const getTimeSelectorFieldKey = ({
    group,
    unit
}: TimeSelectorFieldKeyConstantType): TimeSelectorFieldKey => {
    return `${group}-${unit}`;
};

export const getTimeSelectorGroupDate = ({
    dateRange,
    group,
    date
}: TimeSelectorGroupDateConstantType): Date | undefined => {
    if(group === "single") {
        return date;
    }

    return dateRange?.[group];
};

export const getTimeSelectorFallbackDate = ({
    dateRange,
    group
}: TimeSelectorFallbackDateConstantType): Date | undefined => {
    if(group === "start") {
        return dateRange?.end;
    }

    if(group === "end") {
        return dateRange?.start;
    }

    return undefined;
};

export const getTimeSelectorDayPeriod = ({
    dayPeriod,
    date
}: TimeSelectorDayPeriodConstantType): TimeSelectorDayPeriod => {
    if(!date) {
        return dayPeriod ?? "am";
    }

    return date.getHours() >= TIME_SELECTOR_HALF_DAY_HOURS ? "pm" : "am";
};

export const getTimeSelectorUnitValue = ({
    isWorkWith24HoursFormat = true,
    unit,
    date
}: TimeSelectorUnitValueConstantType): number | undefined => {
    if(!date) {
        return undefined;
    }

    const value = moment(date).get(TIME_SELECTOR_MOMENT_UNITS[unit]);

    if(unit === "hours" && !isWorkWith24HoursFormat) {
        return value % TIME_SELECTOR_HALF_DAY_HOURS || TIME_SELECTOR_HALF_DAY_HOURS;
    }

    return value;
};

export const getTimeSelectorUnitText = ({
    value
}: TimeSelectorUnitTextConstantType): string => {
    if(value === undefined) {
        return "";
    }

    return String(value).padStart(TIME_INPUT_DIGIT_COUNT, "0");
};

export const getTimeSelectorUnitMaximum = ({
    isWorkWith24HoursFormat,
    unit
}: TimeSelectorUnitMaximumConstantType): number => {
    if(unit === "hours" && !isWorkWith24HoursFormat) {
        return TIME_SELECTOR_TWELVE_HOUR_MAXIMUM;
    }

    return TIME_SELECTOR_UNIT_MAXIMUMS[unit];
};

export const getTimeSelectorActiveDraft = ({
    isFocused,
    draft,
    value
}: TimeSelectorActiveDraftConstantType): TimeSelectorDraft | undefined => {
    if(!draft || !isFocused && draft.baseValue !== value) {
        return undefined;
    }

    return draft;
};

export const getTimeSelectorGranularity = ({
    isWorkWithSeconds
}: TimeSelectorGranularityConstantType): TimeSelectorGranularity => {
    return isWorkWithSeconds ? "second" : "minute";
};

export const getTimeSelectorCandidate = ({
    isWorkWith24HoursFormat = true,
    fallbackDate,
    dayPeriod,
    values,
    date
}: TimeSelectorCandidateConstantType): Date | undefined => {
    const units = Object.keys(values) as Array<TimeInputTimeType>;

    if(!units.length) {
        return date;
    }

    const candidate = date ? moment(date) : moment(fallbackDate).startOf("day");

    units.forEach((unit) => {
        const value = values[unit] ?? 0;

        const isTwelveHourHours = unit === "hours" && !isWorkWith24HoursFormat;

        const offset = isTwelveHourHours && dayPeriod === "pm" ? TIME_SELECTOR_HALF_DAY_HOURS : 0;

        candidate.set(TIME_SELECTOR_MOMENT_UNITS[unit], (isTwelveHourHours ? value % TIME_SELECTOR_HALF_DAY_HOURS : value) + offset);
    });

    if(!date && !isWorkWith24HoursFormat && dayPeriod === "pm" && values.hours === undefined) {
        candidate.set(TIME_SELECTOR_MOMENT_UNITS.hours, TIME_SELECTOR_HALF_DAY_HOURS);
    }

    return candidate.toDate();
};

export const isSameTimeSelectorDate = ({
    second,
    first
}: TimeSelectorSameDateConstantType): boolean => {
    return first?.getTime() === second?.getTime();
};

export const getTimeSelectorGroupError = ({
    invalidUnits,
    granularity,
    candidate,
    maxDate,
    minDate
}: TimeSelectorGroupErrorConstantType): TimeSelectorGroupError | undefined => {
    const [
        invalidUnit
    ] = invalidUnits;

    if(invalidUnit) {
        return {
            unit: invalidUnit,
            type: "unitRange"
        };
    }

    if(!candidate) {
        return undefined;
    }

    if(minDate && moment(candidate).isBefore(minDate, granularity)) {
        return {
            type: "minDate"
        };
    }

    if(maxDate && moment(candidate).isAfter(maxDate, granularity)) {
        return {
            type: "maxDate"
        };
    }

    return undefined;
};

export const getTimeSelectorGroupEvaluation = ({
    isWorkWith24HoursFormat,
    fallbackDate,
    focusedField,
    granularity,
    dayPeriod,
    maxDate,
    minDate,
    drafts,
    group,
    units,
    date
}: TimeSelectorGroupEvaluationConstantType): TimeSelectorGroupEvaluation => {
    const values: UpdateDateType = {};

    const currentDayPeriod = getTimeSelectorDayPeriod({
        dayPeriod,
        date
    });

    const invalidUnits: Array<TimeInputTimeType> = [];

    const draftUnits: Array<TimeInputTimeType> = [];

    let isDirty = false;

    const texts = units.map((unit) => {
        const key = getTimeSelectorFieldKey({
            group,
            unit
        });

        const value = getTimeSelectorUnitValue({
            isWorkWith24HoursFormat,
            unit,
            date
        });

        const draft = getTimeSelectorActiveDraft({
            isFocused: focusedField === key,
            draft: drafts[key],
            value
        });

        if(!draft) {
            return getTimeSelectorUnitText({
                value
            });
        }

        draftUnits.push(unit);

        if(!draft.text) {
            return draft.text;
        }

        const draftValue = Number(draft.text);

        if(draftValue !== value) {
            isDirty = true;
        }

        if(Number.isNaN(draftValue) || draftValue > getTimeSelectorUnitMaximum({
            isWorkWith24HoursFormat,
            unit
        })) {
            invalidUnits.push(unit);
        } else {
            values[unit] = draftValue;
        }

        return draft.text;
    });

    const candidate = getTimeSelectorCandidate({
        dayPeriod: currentDayPeriod,
        isWorkWith24HoursFormat,
        fallbackDate,
        values,
        date
    });

    const error = getTimeSelectorGroupError({
        invalidUnits,
        granularity,
        candidate,
        maxDate,
        minDate
    });

    return {
        fields: units.map((unit, index) => ({
            isInvalid: !!error && (error.type !== "unitRange" || invalidUnits.includes(unit)),
            isDraft: draftUnits.includes(unit),
            key: getTimeSelectorFieldKey({
                group,
                unit
            }),
            text: texts[index] ?? "",
            unit
        })),
        value: error ? date : candidate ?? date,
        dayPeriod: currentDayPeriod,
        candidate,
        isDirty,
        error,
        group,
        date
    };
};

export const getTimeSelectorRangeError = ({
    multipleSelectMinimumRequireMS,
    multipleSelectMaximumMS,
    granularity,
    start,
    end
}: TimeSelectorRangeErrorConstantType): TimeSelectorRangeError | undefined => {
    if(!start.value || !end.value) {
        return undefined;
    }

    const startMoment = moment(start.value).startOf(granularity);

    const endMoment = moment(end.value).startOf(granularity);

    const dirtyGroups = [
        start,
        end
    ].filter((groupEvaluation) => groupEvaluation.isDirty && !groupEvaluation.error).map((groupEvaluation) => groupEvaluation.group);

    const invalidGroups: Array<TimeSelectorGroupType> = dirtyGroups.length ? dirtyGroups : [
        end.group
    ];

    if(endMoment.isBefore(startMoment)) {
        return {
            type: invalidGroups.length === 1 && invalidGroups[0] === start.group ? "startAfterEnd" : "endBeforeStart",
            invalidGroups
        };
    }

    const gap = endMoment.diff(startMoment);

    if(multipleSelectMinimumRequireMS && gap < multipleSelectMinimumRequireMS) {
        return {
            type: "minimumGap",
            invalidGroups
        };
    }

    if(multipleSelectMaximumMS && gap > multipleSelectMaximumMS) {
        return {
            type: "maximumGap",
            invalidGroups
        };
    }

    return undefined;
};

export const getTimeSelectorEvaluation = ({
    multipleSelectMinimumRequireMS,
    isWorkWith24HoursFormat,
    multipleSelectMaximumMS,
    isWorkWithSeconds,
    focusedField,
    dayPeriods,
    dateRange,
    maxDate,
    minDate,
    variant,
    drafts,
    date
}: TimeSelectorEvaluationConstantType): TimeSelectorEvaluation => {
    const granularity = getTimeSelectorGranularity({
        isWorkWithSeconds
    });

    const units = getTimeSelectorUnits({
        isWorkWithSeconds
    });

    const groups = getTimeSelectorGroups({
        variant
    }).map((group) => getTimeSelectorGroupEvaluation({
        fallbackDate: getTimeSelectorFallbackDate({
            dateRange,
            group
        }),
        date: getTimeSelectorGroupDate({
            dateRange,
            group,
            date
        }),
        dayPeriod: dayPeriods?.[group],
        isWorkWith24HoursFormat,
        focusedField,
        granularity,
        maxDate,
        minDate,
        drafts,
        group,
        units
    }));

    const [
        start,
        end
    ] = groups;

    if(!start || !end) {
        return {
            groups
        };
    }

    const rangeError = getTimeSelectorRangeError({
        multipleSelectMinimumRequireMS,
        multipleSelectMaximumMS,
        granularity,
        start,
        end
    });

    if(!rangeError) {
        return {
            groups
        };
    }

    return {
        groups: groups.map((groupEvaluation) => {
            if(!rangeError.invalidGroups.includes(groupEvaluation.group)) {
                return groupEvaluation;
            }

            return {
                ...groupEvaluation,
                fields: groupEvaluation.fields.map((field) => ({
                    ...field,
                    isInvalid: true
                }))
            };
        }),
        rangeError
    };
};

export const getTimeSelectorCommit = ({
    evaluation,
    variant
}: TimeSelectorCommitConstantType): TimeSelectorCommit | undefined => {
    const [
        first,
        second
    ] = evaluation.groups;

    if(!first) {
        return undefined;
    }

    if(variant !== "range" || !second) {
        if(first.error || !first.value || isSameTimeSelectorDate({
            second: first.date,
            first: first.value
        })) {
            return undefined;
        }

        return {
            date: first.value
        };
    }

    if(evaluation.rangeError) {
        return undefined;
    }

    if(isSameTimeSelectorDate({
        second: first.date,
        first: first.value
    }) && isSameTimeSelectorDate({
        second: second.date,
        first: second.value
    })) {
        return undefined;
    }

    return {
        dateRange: {
            start: first.value,
            end: second.value
        }
    };
};

export const getTimeSelectorDayPeriodShift = ({
    multipleSelectMinimumRequireMS,
    multipleSelectMaximumMS,
    isWorkWithSeconds,
    groupEvaluation,
    evaluation,
    dayPeriod,
    maxDate,
    variant,
    minDate
}: TimeSelectorDayPeriodShiftConstantType): TimeSelectorDayPeriodShift => {
    const date = groupEvaluation.date;

    if(!date || groupEvaluation.dayPeriod === dayPeriod) {
        return {};
    }

    const candidate = moment(date).add(dayPeriod === "pm" ? TIME_SELECTOR_HALF_DAY_HOURS : -TIME_SELECTOR_HALF_DAY_HOURS, "hours").toDate();

    const granularity = getTimeSelectorGranularity({
        isWorkWithSeconds
    });

    const error = getTimeSelectorGroupError({
        invalidUnits: [],
        granularity,
        candidate,
        maxDate,
        minDate
    });

    if(error) {
        return {
            candidate,
            error
        };
    }

    if(variant !== "range") {
        return {
            commit: {
                date: candidate
            }
        };
    }

    const [
        first,
        second
    ] = evaluation.groups.map((current) => current.group === groupEvaluation.group ? {
        ...current,
        value: candidate
    } : current);

    if(!first || !second) {
        return {};
    }

    const rangeError = getTimeSelectorRangeError({
        multipleSelectMinimumRequireMS,
        multipleSelectMaximumMS,
        start: first,
        granularity,
        end: second
    });

    if(rangeError) {
        return {
            rangeError
        };
    }

    return {
        commit: {
            dateRange: {
                start: first.value,
                end: second.value
            }
        }
    };
};

export const getTimeSelectorNextDate = ({
    groupEvaluation,
    commit
}: TimeSelectorNextDateConstantType): Date | undefined => {
    if(!commit) {
        return groupEvaluation.date;
    }

    if(groupEvaluation.group === "single") {
        return commit.date;
    }

    return commit.dateRange?.[groupEvaluation.group];
};

export const isTimeSelectorDraftSettled = ({
    isInvalid,
    draft,
    value
}: TimeSelectorSettledDraftConstantType): boolean => {
    if(!draft.text || draft.baseValue !== value) {
        return true;
    }

    return !isInvalid && Number(draft.text) === value;
};

export const getTimeSelectorNextDrafts = ({
    isWorkWith24HoursFormat,
    focusedField,
    evaluation,
    drafts,
    commit
}: TimeSelectorNextDraftsConstantType): TimeSelectorDrafts => {
    const nextDrafts: TimeSelectorDrafts = {};

    evaluation.groups.forEach((groupEvaluation) => {
        const nextDate = getTimeSelectorNextDate({
            groupEvaluation,
            commit
        });

        groupEvaluation.fields.forEach((field) => {
            const draft = drafts[field.key];

            if(!draft || !field.isDraft) {
                return;
            }

            const value = getTimeSelectorUnitValue({
                isWorkWith24HoursFormat,
                unit: field.unit,
                date: nextDate
            });

            const nextDraft: TimeSelectorDraft = {
                baseValue: commit ? value : draft.baseValue,
                text: draft.text
            };

            if(field.key !== focusedField && isTimeSelectorDraftSettled({
                isInvalid: field.isInvalid,
                draft: nextDraft,
                value
            })) {
                return;
            }

            nextDrafts[field.key] = nextDraft;
        });
    });

    return nextDrafts;
};

export const isSameTimeSelectorDrafts = ({
    second,
    first
}: TimeSelectorSameDraftsConstantType): boolean => {
    const firstKeys = Object.keys(first) as Array<TimeSelectorFieldKey>;

    if(firstKeys.length !== Object.keys(second).length) {
        return false;
    }

    return firstKeys.every((key) => first[key]?.text === second[key]?.text && first[key]?.baseValue === second[key]?.baseValue);
};

export const getTimeSelectorValueKey = ({
    dateRange,
    date
}: TimeSelectorValueKeyConstantType): string => {
    return [
        date,
        dateRange?.start,
        dateRange?.end
    ].map((value) => value ? value.getTime() : "").join("-");
};

export const getTimeSelectorLimitText = ({
    isWorkWith24HoursFormat,
    isWorkWithSeconds,
    locale,
    limit,
    date
}: TimeSelectorLimitTextConstantType): string => {
    const options: Intl.DateTimeFormatOptions = {
        ...DATE_TIME_FORMAT_OPTIONS[date && moment(date).isSame(limit, "day") ? "time" : "dateTime"],
        hourCycle: isWorkWith24HoursFormat ? "h23" : "h12"
    };

    if(isWorkWithSeconds) {
        options.second = "2-digit";
    }

    return formatDateTime({
        date: limit,
        options,
        locale
    });
};

export const getTimeSelectorDurationText = ({
    locale,
    value
}: TimeSelectorDurationTextConstantType): string => {
    return moment.duration(value).locale(getMomentLocale({
        locale
    })).humanize();
};

export const getTimeSelectorLabelColor = ({
    isDisabled,
    colors
}: TimeSelectorLabelColorConstantType): string => {
    if(isDisabled) {
        return colors.system.state.content.disabled.neutral;
    }

    return colors.content.text.high;
};

const stylesheet = StyleSheet.create({
    inlineGroup: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        flexGrow: 1
    },
    groupsContainer: {
        alignItems: "flex-start",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    helper: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    fieldsContainer: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        maxWidth: "100%",
        ...Platform.select({
            default: {
                direction: "ltr"
            },
            web: {
            }
        })
    },
    helperIcon: {
        justifyContent: "center"
    },
    container: {
        flexDirection: "column",
        width: "100%"
    },
    group: {
        flexDirection: "column",
        maxWidth: "100%"
    },
    helperText: {
        flexShrink: 1
    },
    label: {
        flexShrink: 1
    }
});

export const useStyles = ({
    labelPosition,
    isCentered,
    typography,
    variant,
    spaces
}: TimeSelectorDynamicStyle) => {
    const styles = {
        helperIcon: {
            height: typography.bodyMediumSize.lineHeight
        } as Mutable<ViewStyle>,
        dayPeriod: {
            marginStart: spaces.spacingSm
        } as Mutable<ViewStyle>,
        container: {
            marginTop: spaces.spacingMd,
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        groupsContainer: {
            gap: spaces.spacingLg
        } as Mutable<ViewStyle>,
        fieldsContainer: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        helper: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        group: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>
    };

    if(labelPosition === "start") {
        styles.groupsContainer.justifyContent = "space-between";

        styles.groupsContainer.gap = spaces.spacingMd;

        delete styles.container.marginTop;
    }

    if(labelPosition === "start" && variant !== "range") {
        Object.assign(styles.group, stylesheet.inlineGroup);

        styles.group.gap = spaces.spacingMd;
    }

    if(labelPosition === "top" && isCentered) {
        styles.container.alignItems = "center";
    }

    return styles;
};
export default stylesheet;
